import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  ChartBarIcon,
  FolderIcon,
  TagIcon,
  PlusIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

import { diaryService } from '../../services/diary';

const Sidebar = () => {
  const location = useLocation();
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    fetchSidebarData();
  }, []);

  const fetchSidebarData = async () => {
    try {
      const [foldersData, tagsData, statsData] = await Promise.all([
        diaryService.getFolders(),
        diaryService.getTags(),
        diaryService.getStats()
      ]);
      
      setFolders(foldersData.slice(0, 5)); // Show top 5 folders
      setTags(tagsData.slice(0, 8)); // Show top 8 tags
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch sidebar data:', error);
    }
  };

  const navigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'All Entries', href: '/entries', icon: BookOpenIcon },
    { name: 'Search', href: '/search', icon: MagnifyingGlassIcon },
    { name: 'Shares', href: '/shares', icon: ShareIcon },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  ];

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <motion.aside
      className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 transition-all duration-300 z-30 ${
        isCollapsed ? 'w-16' : 'w-64'
      } hidden lg:block`}
      initial={{ x: -100 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col h-full">
        {/* Toggle Button */}
        <div className="p-4 border-b border-gray-200">
          <motion.button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              animate={{ rotate: isCollapsed ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              ←
            </motion.div>
          </motion.button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Quick Actions */}
          <div>
            <Link
              to="/entries/new"
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              {!isCollapsed && <span>New Entry</span>}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {!isCollapsed && (
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Navigation
              </h3>
            )}
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={isCollapsed ? item.name : undefined}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="font-medium">{item.name}</span>}
              </Link>
            ))}
          </nav>

          {/* Quick Stats */}
          {!isCollapsed && stats && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Entries</span>
                  <span className="font-medium">{stats.overview.totalEntries}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">This Month</span>
                  <span className="font-medium">12</span> {/* You can calculate this */}
                </div>
              </div>
            </div>
          )}

          {/* Folders */}
          {!isCollapsed && folders.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Folders
                </h3>
                <Link
                  to="/folders"
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-1">
                {folders.map((folder) => (
                  <Link
                    key={folder}
                    to={`/entries?folder=${encodeURIComponent(folder)}`}
                    className="flex items-center space-x-2 px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <FolderIcon className="h-4 w-4 text-primary-500" />
                    <span className="truncate">{folder}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {!isCollapsed && tags.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Popular Tags
                </h3>
                <Link
                  to="/tags"
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  View all
                </Link>
              </div>
              <div className="flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?tag=${encodeURIComponent(tag)}`}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 hover:bg-primary-100 hover:text-primary-700 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isCollapsed && (
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              <p>DiaryVerse v1.0</p>
              <p>Made with ❤️</p>
            </div>
          </div>
        )}
      </div>
    </motion.aside>
  );
};

export default Sidebar;
